import type { MarketingLocale } from "@/lib/seo/site";

/** Press kit UI language. Catalan is the default for /press. */
export const PRESS_LANGS = ["ca", "es", "en"] as const;
export type PressLang = (typeof PRESS_LANGS)[number];

export function parsePressLang(value: string | string[] | undefined | null): PressLang {
  const raw = Array.isArray(value) ? value[0] : value;
  if (raw === "es" || raw === "en" || raw === "ca") return raw;
  return "ca";
}

/** Public path for a press-kit language. Catalan has no query string. */
export function pressPath(lang: PressLang): string {
  if (lang === "ca") return "/press";
  return `/press?lang=${lang}`;
}

export function pressAbsoluteUrl(siteUrl: string, lang: PressLang): string {
  return `${siteUrl.replace(/\/$/, "")}${pressPath(lang)}`;
}

export const PRESS_REDIRECTS = [
  { source: "/premsa", destination: "/press", lang: "ca" },
  { source: "/prensa", destination: "/press?lang=es", lang: "es" },
] as const;

export const PRESS_ZIP = {
  file: "menuhunter-press-kit.zip",
  href: "/press/menuhunter-press-kit.zip",
} as const;

export type PressAsset = {
  id: string;
  file: string;
  href: string;
  /** Catalan screenshots are also served for press emails. */
  emailHref?: string;
  kind: "logo" | "screenshot";
  framed?: boolean;
  /** Stand-in image until the final PNG is added at the same URL. */
  placeholder?: boolean;
  previewOnDark?: boolean;
};

const LOGO_FILES = [
  { id: "logo-full-color", file: "menuhunter-logo-full-color.png" },
  { id: "logo-white", file: "menuhunter-logo-white.png", previewOnDark: true },
  { id: "app-icon", file: "menuhunter-app-icon-1024.png" },
  { id: "mark-svg", file: "menuhunter-mark.svg" },
] as const;

export const PRESS_LOGOS: PressAsset[] = LOGO_FILES.map((item) => ({
  id: item.id,
  file: item.file,
  href: `/press/logos/${item.file}`,
  kind: "logo" as const,
  previewOnDark: "previewOnDark" in item ? item.previewOnDark : false,
}));

const SCREEN_NAMES = ["map", "list", "piccolo", "home"] as const;

function screenshotAsset(
  locale: "ca" | "es",
  name: (typeof SCREEN_NAMES)[number],
  framed: boolean
): PressAsset {
  const variant = framed ? "framed" : "unframed";
  const file = `${name}-${locale}-${variant}.png`;
  return {
    id: `${name}-${locale}-${variant}`,
    file,
    href: `/press/screenshots/${locale}/${file}`,
    emailHref: locale === "ca" ? `/email/press/${file}` : undefined,
    kind: "screenshot",
    framed,
    placeholder: locale !== "ca",
  };
}

export const PRESS_SCREENSHOTS_CA: PressAsset[] = SCREEN_NAMES.flatMap((name) => [
  screenshotAsset("ca", name, true),
  screenshotAsset("ca", name, false),
]);

export const PRESS_SCREENSHOTS_ES: PressAsset[] = SCREEN_NAMES.flatMap((name) => [
  screenshotAsset("es", name, true),
  screenshotAsset("es", name, false),
]);

export const PRESS_BRAND_COLORS = [
  { id: "teal", hex: "#0FA895" },
  { id: "orange", hex: "#D05A3F" },
  { id: "dark", hex: "#020F18" },
] as const;

type PressCopy = {
  metaTitle: string;
  boilerplate: string;
  eyebrow: string;
  foundersTitle: string;
  foundersBody: string;
  contactLabel: string;
  downloadsTitle: string;
  downloadsIntro: string;
  logosTitle: string;
  screenshotsCaTitle: string;
  screenshotsEsTitle: string;
  zipTitle: string;
  zipBody: string;
  download: string;
  downloadAll: string;
  placeholder: string;
  colorsTitle: string;
  colorsIntro: string;
  socialTitle: string;
  appTitle: string;
  appBody: string;
  ios: string;
  android: string;
  languageLabel: string;
  assetNames: Record<string, string>;
  colorNames: Record<(typeof PRESS_BRAND_COLORS)[number]["id"], string>;
};

const ASSET_NAMES: Record<PressLang, Record<string, string>> = {
  ca: {
    "logo-full-color": "Logotip a tot color (PNG)",
    "logo-white": "Logotip blanc (PNG)",
    "app-icon": "Icona de l'app (PNG, 1024 px)",
    "mark-svg": "Marca (SVG)",
    "map-ca-framed": "Mapa, amb marc",
    "map-ca-unframed": "Mapa, sense marc",
    "list-ca-framed": "Llista, amb marc",
    "list-ca-unframed": "Llista, sense marc",
    "piccolo-ca-framed": "Piccolo, amb marc",
    "piccolo-ca-unframed": "Piccolo, sense marc",
    "home-ca-framed": "Inici, amb marc",
    "home-ca-unframed": "Inici, sense marc",
    "map-es-framed": "Mapa, amb marc (castellà)",
    "map-es-unframed": "Mapa, sense marc (castellà)",
    "list-es-framed": "Llista, amb marc (castellà)",
    "list-es-unframed": "Llista, sense marc (castellà)",
    "piccolo-es-framed": "Piccolo, amb marc (castellà)",
    "piccolo-es-unframed": "Piccolo, sense marc (castellà)",
    "home-es-framed": "Inici, amb marc (castellà)",
    "home-es-unframed": "Inici, sense marc (castellà)",
  },
  es: {
    "logo-full-color": "Logotipo a todo color (PNG)",
    "logo-white": "Logotipo blanco (PNG)",
    "app-icon": "Icono de la app (PNG, 1024 px)",
    "mark-svg": "Marca (SVG)",
    "map-ca-framed": "Mapa, con marco (catalán)",
    "map-ca-unframed": "Mapa, sin marco (catalán)",
    "list-ca-framed": "Lista, con marco (catalán)",
    "list-ca-unframed": "Lista, sin marco (catalán)",
    "piccolo-ca-framed": "Piccolo, con marco (catalán)",
    "piccolo-ca-unframed": "Piccolo, sin marco (catalán)",
    "home-ca-framed": "Inicio, con marco (catalán)",
    "home-ca-unframed": "Inicio, sin marco (catalán)",
    "map-es-framed": "Mapa, con marco",
    "map-es-unframed": "Mapa, sin marco",
    "list-es-framed": "Lista, con marco",
    "list-es-unframed": "Lista, sin marco",
    "piccolo-es-framed": "Piccolo, con marco",
    "piccolo-es-unframed": "Piccolo, sin marco",
    "home-es-framed": "Inicio, con marco",
    "home-es-unframed": "Inicio, sin marco",
  },
  en: {
    "logo-full-color": "Full colour logo (PNG)",
    "logo-white": "White logo (PNG)",
    "app-icon": "App icon (PNG, 1024 px)",
    "mark-svg": "Mark (SVG)",
    "map-ca-framed": "Map, phone-framed (Catalan)",
    "map-ca-unframed": "Map, unframed (Catalan)",
    "list-ca-framed": "List, phone-framed (Catalan)",
    "list-ca-unframed": "List, unframed (Catalan)",
    "piccolo-ca-framed": "Piccolo, phone-framed (Catalan)",
    "piccolo-ca-unframed": "Piccolo, unframed (Catalan)",
    "home-ca-framed": "Home, phone-framed (Catalan)",
    "home-ca-unframed": "Home, unframed (Catalan)",
    "map-es-framed": "Map, phone-framed (Spanish)",
    "map-es-unframed": "Map, unframed (Spanish)",
    "list-es-framed": "List, phone-framed (Spanish)",
    "list-es-unframed": "List, unframed (Spanish)",
    "piccolo-es-framed": "Piccolo, phone-framed (Spanish)",
    "piccolo-es-unframed": "Piccolo, unframed (Spanish)",
    "home-es-framed": "Home, phone-framed (Spanish)",
    "home-es-unframed": "Home, unframed (Spanish)",
  },
};

export const PRESS_COPY: Record<PressLang, PressCopy> = {
  ca: {
    metaTitle: "MenúHunter · Kit de premsa",
    boilerplate:
      "MenúHunter és l'app nascuda a Barcelona que posa el menú del dia al mapa. Gairebé 1.500 restaurants mapats (més de 1.100 a Barcelona), menús penjats per la comunitat en 10 segons, i fitxa gratuïta per a restaurants amb «Gestionar ara».",
    eyebrow: "Premsa",
    foundersTitle: "Fundadors",
    foundersBody: "Chris i Gerard.",
    contactLabel: "Contacte de premsa",
    downloadsTitle: "Descàrregues",
    downloadsIntro: "Cada fitxer es descarrega directament.",
    logosTitle: "Logotips",
    screenshotsCaTitle: "Captures en català",
    screenshotsEsTitle: "Captures en castellà",
    zipTitle: "Descarrega-ho tot",
    zipBody: "Logotips i captures en un sol ZIP.",
    download: "Descarrega",
    downloadAll: "Descarrega-ho tot",
    placeholder: "Espai reservat",
    colorsTitle: "Colors de marca",
    colorsIntro: "Verd, taronja i fosc de l'app.",
    socialTitle: "Xarxes",
    appTitle: "Descarrega l'app",
    appBody: "iOS i Android, a menuhunter.app/download.",
    ios: "Descarrega per a iOS",
    android: "Descarrega per a Android",
    languageLabel: "Idioma",
    assetNames: ASSET_NAMES.ca,
    colorNames: { teal: "Verd", orange: "Taronja", dark: "Fosc" },
  },
  es: {
    metaTitle: "MenúHunter · Kit de prensa",
    boilerplate:
      "MenúHunter es la app nacida en Barcelona que pone el menú del día en el mapa. Casi 1.500 restaurantes mapeados (más de 1.100 en Barcelona), menús subidos por la comunidad en 10 segundos y ficha gratuita para restaurantes con «Gestionar ahora».",
    eyebrow: "Prensa",
    foundersTitle: "Fundadores",
    foundersBody: "Chris y Gerard.",
    contactLabel: "Contacto de prensa",
    downloadsTitle: "Descargas",
    downloadsIntro: "Cada archivo se descarga directamente.",
    logosTitle: "Logotipos",
    screenshotsCaTitle: "Capturas en catalán",
    screenshotsEsTitle: "Capturas en español",
    zipTitle: "Descargar todo",
    zipBody: "Logotipos y capturas en un solo ZIP.",
    download: "Descargar",
    downloadAll: "Descargar todo",
    placeholder: "Marcador de posición",
    colorsTitle: "Colores de marca",
    colorsIntro: "Verde, naranja y oscuro de la app.",
    socialTitle: "Redes",
    appTitle: "Descarga la app",
    appBody: "iOS y Android, en menuhunter.app/download.",
    ios: "Descargar para iOS",
    android: "Descargar para Android",
    languageLabel: "Idioma",
    assetNames: ASSET_NAMES.es,
    colorNames: { teal: "Verde", orange: "Naranja", dark: "Oscuro" },
  },
  en: {
    metaTitle: "MenúHunter · Press kit",
    boilerplate:
      'MenúHunter is the Barcelona-born app that puts the menú del día on the map. Almost 1,500 restaurants mapped (more than 1,100 in Barcelona), menus uploaded by the community in 10 seconds, and a free listing for restaurants with "Manage now".',
    eyebrow: "Press",
    foundersTitle: "Founders",
    foundersBody: "Chris and Gerard.",
    contactLabel: "Press contact",
    downloadsTitle: "Downloads",
    downloadsIntro: "Every file downloads directly.",
    logosTitle: "Logos",
    screenshotsCaTitle: "Catalan screenshots",
    screenshotsEsTitle: "Spanish screenshots",
    zipTitle: "Download everything",
    zipBody: "Logos and screenshots in one ZIP.",
    download: "Download",
    downloadAll: "Download everything",
    placeholder: "Placeholder",
    colorsTitle: "Brand colours",
    colorsIntro: "Teal, orange, and the app dark.",
    socialTitle: "Social",
    appTitle: "Download the app",
    appBody: "iOS and Android, at menuhunter.app/download.",
    ios: "Download for iOS",
    android: "Download for Android",
    languageLabel: "Language",
    assetNames: ASSET_NAMES.en,
    colorNames: { teal: "Teal", orange: "Orange", dark: "Dark" },
  },
};

export function isPressLang(value: MarketingLocale): value is PressLang {
  return (PRESS_LANGS as readonly string[]).includes(value);
}
